import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface FeatureRequest {
  id: number;
  title: string;
  description: string | null;
  author_name: string | null;
  vote_count: number;
  status: 'open' | 'in_progress' | 'done' | 'rejected';
  created_at: string;
}

export interface FeatureVote {
  id: number;
  feature_id: number;
  user_id: string;
  created_at: string;
}

// Fetch all feature requests
export function useFeatures() {
  return useQuery({
    queryKey: ['features'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_requests')
        .select('id, title, description, author_name, vote_count, status, created_at')
        .order('vote_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as FeatureRequest[];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// Fetch user's votes
export function useUserVotes() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feature-votes', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('feature_votes')
        .select('feature_id')
        .eq('user_id', user.id);

      if (error) throw error;
      return (data || []).map((v) => v.feature_id);
    },
    enabled: !!user,
    staleTime: 1000 * 60, // 1 minute
  });
}

// Create a new feature request
export function useCreateFeature() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ title, description }: { title: string; description?: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('feature_requests')
        .insert({
          title,
          description: description || null,
          author_id: user.id,
          author_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
        })
        .select('id, title, description, author_name, vote_count, status, created_at')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
    },
  });
}

// Vote for a feature
export function useVoteFeature() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (featureId: number) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('feature_votes')
        .insert({
          feature_id: featureId,
          user_id: user.id,
        });

      if (error) throw error;
      return featureId;
    },
    onMutate: async (featureId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['features'] });
      await queryClient.cancelQueries({ queryKey: ['feature-votes', user?.id] });

      // Snapshot previous values
      const previousFeatures = queryClient.getQueryData<FeatureRequest[]>(['features']);
      const previousVotes = queryClient.getQueryData<number[]>(['feature-votes', user?.id]);

      // Optimistically update features
      queryClient.setQueryData<FeatureRequest[]>(['features'], (old) =>
        old?.map((f) => (f.id === featureId ? { ...f, vote_count: f.vote_count + 1 } : f))
      );

      // Optimistically update user votes
      queryClient.setQueryData<number[]>(['feature-votes', user?.id], (old) =>
        old ? [...old, featureId] : [featureId]
      );

      return { previousFeatures, previousVotes };
    },
    onError: (_err, _featureId, context) => {
      // Rollback on error
      if (context?.previousFeatures) {
        queryClient.setQueryData(['features'], context.previousFeatures);
      }
      if (context?.previousVotes) {
        queryClient.setQueryData(['feature-votes', user?.id], context.previousVotes);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['feature-votes'] });
    },
  });
}

// Unvote for a feature
export function useUnvoteFeature() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (featureId: number) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('feature_votes')
        .delete()
        .eq('feature_id', featureId)
        .eq('user_id', user.id);

      if (error) throw error;
      return featureId;
    },
    onMutate: async (featureId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['features'] });
      await queryClient.cancelQueries({ queryKey: ['feature-votes', user?.id] });

      // Snapshot previous values
      const previousFeatures = queryClient.getQueryData<FeatureRequest[]>(['features']);
      const previousVotes = queryClient.getQueryData<number[]>(['feature-votes', user?.id]);

      // Optimistically update features
      queryClient.setQueryData<FeatureRequest[]>(['features'], (old) =>
        old?.map((f) => (f.id === featureId ? { ...f, vote_count: Math.max(0, f.vote_count - 1) } : f))
      );

      // Optimistically update user votes
      queryClient.setQueryData<number[]>(['feature-votes', user?.id], (old) =>
        old?.filter((id) => id !== featureId) ?? []
      );

      return { previousFeatures, previousVotes };
    },
    onError: (_err, _featureId, context) => {
      // Rollback on error
      if (context?.previousFeatures) {
        queryClient.setQueryData(['features'], context.previousFeatures);
      }
      if (context?.previousVotes) {
        queryClient.setQueryData(['feature-votes', user?.id], context.previousVotes);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['features'] });
      queryClient.invalidateQueries({ queryKey: ['feature-votes'] });
    },
  });
}

